/**
 * Helpers de formato de moneda con soporte multi-divisa.
 *
 * El sistema acepta CFDIs en cualquier moneda válida del catálogo
 * c_Moneda del SAT (MXN, USD, EUR, CAD, etc.) · cada CFDI se trabaja en
 * SU moneda · sin conversión. Por eso TODO display de un monto debe
 * pasar por estos helpers, nunca un `$` literal ni un `currency: "MXN"`
 * hardcoded.
 *
 * `Intl.NumberFormat` cachea internamente, así que crear el formatter
 * en cada llamada es barato. Si queremos optimizar más adelante,
 * podemos memoizar por par (locale, moneda) en un Map.
 */

const LOCALE_DEFAULT = "es-MX";

/**
 * Formatea un monto numérico en la moneda dada · ISO 4217 (MXN, USD, EUR).
 * Si la moneda viene null/inválida, hace fallback a número sin símbolo.
 *
 * Por defecto muestra el código ISO 4217 de la moneda (MXN, USD, EUR) en lugar
 * del símbolo, para evitar ambigüedad (en es-MX tanto MXN como USD se rinden con
 * "$"). Se puede forzar el símbolo con `currencyDisplay: "symbol"`.
 *
 * @example
 *   formatearMonto(1234.56, "MXN") → "MXN 1,234.56"
 *   formatearMonto(1234.56, "USD") → "USD 1,234.56"
 *   formatearMonto(1234.56, "EUR") → "EUR 1,234.56"
 */
export function formatearMonto(
  monto: number,
  moneda: string | null | undefined,
  opts: {
    maximumFractionDigits?: number;
    minimumFractionDigits?: number;
    /**
     * Cómo mostrar la divisa · "code" (default, ej. "MXN 1,234" · ISO 4217) o
     * "symbol" (ej. "$1,234"). El default es "code" para evitar el "$" ambiguo
     * (en es-MX tanto MXN como USD se rinden con "$").
     */
    currencyDisplay?: "symbol" | "narrowSymbol" | "code" | "name";
  } = {},
): string {
  const codigo = (moneda ?? "").trim().toUpperCase();
  if (!codigo) {
    return monto.toLocaleString(LOCALE_DEFAULT, {
      maximumFractionDigits: opts.maximumFractionDigits ?? 2,
      minimumFractionDigits: opts.minimumFractionDigits ?? 0,
    });
  }
  try {
    return new Intl.NumberFormat(LOCALE_DEFAULT, {
      style: "currency",
      currency: codigo,
      currencyDisplay: opts.currencyDisplay ?? "code",
      maximumFractionDigits: opts.maximumFractionDigits ?? 2,
      minimumFractionDigits: opts.minimumFractionDigits ?? 0,
    }).format(monto);
  } catch {
    // Intl tira RangeError si el código no es ISO 4217 válido · fallback.
    return `${codigo} ${monto.toLocaleString(LOCALE_DEFAULT, {
      maximumFractionDigits: opts.maximumFractionDigits ?? 2,
      minimumFractionDigits: opts.minimumFractionDigits ?? 0,
    })}`;
  }
}
